// =============================================================================
// Leaderboard.tsx — Leaderboard screen
// Project: Texas Hold'em Social Casino
// =============================================================================
// Shows global chip leaderboard and level leaderboard.
// Data comes from D1 via the Worker API, cached in KV.

import React, { useEffect, useState } from 'react'
import { useUIStore } from '../store/ui-store'

interface LeaderboardEntry {
  id: string
  name: string
  chips: number
  level: number
  totalWins: number
  totalHands?: number
}

const API_BASE = import.meta.env.VITE_API_URL || '/api'

type LeaderboardTab = 'chips' | 'level'

export default function LeaderboardScreen(): React.ReactElement {
  const { navigateTo } = useUIStore()
  const [tab, setTab] = useState<LeaderboardTab>('chips')
  const [entries, setEntries] = useState<LeaderboardEntry[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchLeaderboard = async (): Promise<void> => {
      setLoading(true)
      try {
        const endpoint = tab === 'chips' ? 'leaderboard/global' : 'leaderboard/level'
        const res = await fetch(`${API_BASE}/${endpoint}`)
        const json = await res.json()
        if (json.success) {
          setEntries(json.data || [])
        }
      } catch (err) {
        console.error('Failed to fetch leaderboard:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchLeaderboard()
  }, [tab])

  return (
    <div className="lobby">
      <div className="lobby-header">
        <button className="btn btn-secondary" style={{ padding: '6px 14px', fontSize: 12 }} onClick={() => navigateTo('lobby')}>
          ← Back
        </button>
        <div className="lobby-title">Leaderboard</div>
        <div style={{ width: 60 }} />
      </div>

      {/* Tab switch */}
      <div style={{ display: 'flex', gap: 8, margin: '12px 0' }}>
        <button
          className={`btn ${tab === 'chips' ? 'btn-gold' : 'btn-secondary'}`}
          style={{ flex: 1, fontSize: 12, padding: '8px 12px' }}
          onClick={() => setTab('chips')}
        >
          Chip Ranking
        </button>
        <button
          className={`btn ${tab === 'level' ? 'btn-gold' : 'btn-secondary'}`}
          style={{ flex: 1, fontSize: 12, padding: '8px 12px' }}
          onClick={() => setTab('level')}
        >
          Level Ranking
        </button>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex-center" style={{ flex: 1 }}>Loading...</div>
      ) : entries.length === 0 ? (
        <div className="flex-center" style={{ flex: 1, color: '#666' }}>No data yet</div>
      ) : (
        <div style={{ flex: 1, overflow: 'auto' }}>
          {entries.map((entry, i) => (
            <div key={entry.id} style={{
              display: 'flex', alignItems: 'center',
              padding: '10px 8px',
              borderBottom: '1px solid rgba(255,255,255,0.05)',
            }}>
              <div style={{ width: 30, textAlign: 'center', fontWeight: 700, color: i < 3 ? '#f5c518' : '#666' }}>
                {i + 1}
              </div>
              <div style={{
                width: 32, height: 32, borderRadius: '50%',
                background: '#16213e', border: '2px solid #f5c518',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 12, marginRight: 10,
              }}>
                {entry.name.charAt(0)}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{entry.name}</div>
                <div style={{ fontSize: 11, color: '#888' }}>
                  Lv.{entry.level} · {entry.totalWins} wins
                </div>
              </div>
              <div style={{ color: '#f5c518', fontWeight: 700, fontSize: 14 }}>
                {tab === 'chips'
                  ? entry.chips.toLocaleString()
                  : `Lv.${entry.level}`
                }
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
